// Admin: seasonal opening-hour overrides (e.g. summer pool 08:00–22:00) that switch on by date.
import React, { useEffect, useState } from 'react';
import { CalendarRange, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { apiCall } from './_shared';

const TIME_FIELDS = [['check_in', 'Check-in'], ['check_out', 'Check-out'], ['breakfast_from', 'Breakfast from'], ['breakfast_to', 'Breakfast to'],
  ['pool_from', 'Pool from'], ['pool_to', 'Pool to'], ['whatsapp_from', 'WhatsApp from'], ['whatsapp_to', 'WhatsApp to']];
const toMD = (v) => (v ? v.slice(5) : '');          // yyyy-MM-dd → MM-dd for the date input round-trip
const fromMD = (md) => (md ? `2024-${md}` : '');    // leap year so 02-29 is allowed

const SeasonCard = ({ s, idx, base, active, onChange, onRemove }) => {
  const set = (k, v) => onChange({ ...s, [k]: v });
  const setHour = (k, v) => onChange({ ...s, hours: { ...s.hours, [k]: v } });
  const inputs = TIME_FIELDS.map(([k, label]) => (
    <label key={k} className="text-xs">
      <span className="block text-slate-500 font-semibold mb-0.5">{label}</span>
      <input type="time" value={s.hours[k] || ''} onChange={(e) => setHour(k, e.target.value)} className="w-full border rounded px-2 py-1" data-testid={`season-${idx}-${k}`} />
    </label>
  ));
  return (
    <div className={`border rounded-xl p-4 ${active ? 'border-emerald-400 bg-emerald-50/40' : 'bg-slate-50'}`} data-testid={`season-card-${idx}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={s.name} onChange={(e) => set('name', e.target.value)} placeholder="Season name (e.g. Summer)" className="border rounded px-2 py-1 text-sm font-semibold w-44" data-testid={`season-${idx}-name`} />
        <span className="text-xs text-slate-500">from</span>
        <input type="date" value={fromMD(s.start)} onChange={(e) => set('start', toMD(e.target.value))} className="border rounded px-2 py-1 text-sm" data-testid={`season-${idx}-start`} />
        <span className="text-xs text-slate-500">to</span>
        <input type="date" value={fromMD(s.end)} onChange={(e) => set('end', toMD(e.target.value))} className="border rounded px-2 py-1 text-sm" data-testid={`season-${idx}-end`} />
        <span className="text-[11px] text-slate-400">(every year; may wrap over New Year)</span>
        {active && <span className="text-[10px] font-bold uppercase bg-emerald-600 text-white rounded px-1.5 py-0.5" data-testid={`season-${idx}-active`}>Active today</span>}
        <span className="flex-1" />
        <button type="button" onClick={() => onChange({ ...s, hours: { ...base, reception: s.hours.reception } })} className="text-xs text-slate-500 hover:underline">Copy default times</button>
        <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700" title="Remove season" data-testid={`season-${idx}-remove`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{inputs}</div>
    </div>
  );
};

const SeasonalHours = ({ base }) => {
  const [seasons, setSeasons] = useState(null);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiCall('/api/admin/hotel-info/hours/seasons').then((d) => { setSeasons(d.seasons); setActive(d.active); }).catch((e) => setMsg(e.message));
  }, []);

  const add = () => setSeasons((l) => [...l, { name: l.length ? 'Winter' : 'Summer', start: l.length ? '11-01' : '06-01', end: l.length ? '03-31' : '09-30', hours: { ...base } }]);
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/hotel-info/hours/seasons', { method: 'PUT', body: JSON.stringify({ seasons }) });
      setSeasons(r.seasons); setActive(r.active); setMsg(r.active ? `Saved — "${r.active}" hours are live today.` : 'Saved — default hours apply today.');
    } catch (e) { setMsg(e.message); } finally { setSaving(false); }
  };

  if (!seasons) return null;
  const cards = seasons.map((s, i) => (
    <SeasonCard key={i} s={s} idx={i} base={base} active={active === s.name}
      onChange={(n) => setSeasons((l) => l.map((x, j) => (j === i ? n : x)))}
      onRemove={() => setSeasons((l) => l.filter((_, j) => j !== i))} />
  ));

  return (
    <div className="mt-6 border-t pt-5" data-testid="seasonal-hours">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="font-semibold text-slate-900 flex items-center gap-2 text-sm"><CalendarRange className="w-4 h-4 text-orange-500" /> Seasonal hours <span className="text-xs text-slate-500 font-normal">— override the default times between two dates, automatically every year</span></h4>
        <button type="button" onClick={add} disabled={seasons.length >= 6} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800 border rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50" data-testid="season-add"><Plus className="w-4 h-4" /> Add season</button>
      </div>
      {cards.length === 0 && <p className="text-xs text-slate-500" data-testid="seasons-empty">No seasons yet — the default hours above apply all year.</p>}
      <div className="space-y-3">{cards}</div>
      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-lg px-4 py-2 hover:bg-slate-700 disabled:opacity-60" data-testid="seasons-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save seasons
        </button>
        {msg && <span className="text-sm text-emerald-700" data-testid="seasons-msg">{msg}</span>}
      </div>
    </div>
  );
};

export default SeasonalHours;
